/**
 * LO COMÚN DE «TODAS LAS SUCURSALES» (2026-09-29).
 *
 * Las pantallas del apartado —Resumen, Ventas, Dinero, Inventario, Por cobrar
 * y Por pagar— comparten el período, el color de cada sucursal, la forma de
 * entrar en una y los botones de opciones. Viven aquí para que no puedan
 * contradecirse: si la Norte es ámbar en la gráfica del Resumen, es ámbar
 * también en la barra de su inventario.
 *
 * ⚠️ Todo el apartado trata de usted (lo pidió el dueño, 2026-10-02).
 */

import { useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, TrendingDown, TrendingUp, Warehouse } from "lucide-react";
import { Link } from "react-router-dom";
import { Boton, Segmentado, Vacio, cx } from "@/components/ui";
import { FiltroDeFechas, rangoDelAtajo, type Rango } from "@/components/FiltroDeFechas";
import { useTienePremium } from "@/components/Premium";
import { useCambiarDeSucursal } from "@/components/SucursalActual";
import { monedas } from "@/lib/endpoints";
import {
  comoFormato,
  formatFecha,
  monedaPrincipal,
  MONEDA_POR_DEFECTO,
  toNumber,
} from "@/lib/money";
import { useAuth } from "@/stores/auth";
import { aviso } from "@/stores/ui";
import { useAmbito } from "./ambito";

const LLAVE_RANGO = "softfi-todas-las-sucursales-rango";

/**
 * El período elegido, el MISMO en todas las pestañas mientras dure la visita.
 *
 * Quien mira el mes en el Resumen y pasa a Dinero espera ver el mes, no que
 * vuelva solo a «Este mes» después de haber elegido otra cosa. Se guarda en la
 * sesión del navegador: al volver mañana arranca de nuevo en «Últimos 30
 * días».
 *
 * ⚠️ «Últimos 30 días» y no «Este mes»: el día 2 del mes, «Este mes» son dos
 * días, y comparar dos días contra los dos anteriores dice «−85 %» por un
 * domingo flojo. Treinta días siempre tienen con qué compararse.
 *
 * ⚠️ ES EL FILTRO DE FECHAS DE LA APP (2026-10-02). Antes eran cuatro botones
 * propios —Hoy · Semana · Mes · 30 días— y el dueño ya había pedido lo mismo
 * para las pantallas de dentro: «usa el filtro que ya tenemos». Con él se
 * puede ver «el mes pasado» o «del 3 al 12», que con los botones no se podía.
 */
export function useRango(): [Rango, (r: Rango) => void] {
  const [rango, setRango] = useState<Rango>(() => {
    try {
      const guardado = JSON.parse(sessionStorage.getItem(LLAVE_RANGO) ?? "null") as Rango | null;
      if (guardado?.desde && guardado?.hasta) return guardado;
    } catch {
      /* modo privado o algo roto: arranca en «Últimos 30 días» */
    }
    return rangoDelAtajo("30dias");
  });
  return [
    rango,
    (r) => {
      // Una fecha a medio escribir no se pide: el servidor no sabría qué hacer
      // con «desde: vacío».
      if (!r.desde || !r.hasta) return;
      setRango(r);
      try {
        sessionStorage.setItem(LLAVE_RANGO, JSON.stringify(r));
      } catch {
        /* modo privado: se queda solo en memoria */
      }
    },
  ];
}

/** El filtro de fechas en el encabezado de cada pestaña. */
export function Periodo({ rango, onCambiar }: { rango: Rango; onCambiar: (r: Rango) => void }) {
  return (
    <div className="w-full sm:w-auto">
      <FiltroDeFechas rango={rango} onCambiar={onCambiar} />
    </div>
  );
}

/** Cuántos días abarca el período: decide si la gráfica va por día, semana o mes. */
export function diasDelRango(r: Rango): number {
  const a = new Date(`${r.desde}T12:00:00`).getTime();
  const b = new Date(`${r.hasta}T12:00:00`).getTime();
  return Math.max(1, Math.round((b - a) / 86_400_000) + 1);
}

/** El formato de la moneda de referencia del negocio. */
export function useFormato() {
  const { data } = useQuery({ queryKey: ["monedas-negocio"], queryFn: monedas.listar });
  const principal = data ? monedaPrincipal(data) : undefined;
  return principal ? comoFormato(principal) : MONEDA_POR_DEFECTO;
}

/**
 * El color de una sucursal, por su puesto en la lista (la principal primero).
 * Seis tonos en `index.css` (`--c-serie-*`), en claro y en oscuro.
 */
export function colorDe(indice: number): string {
  return `var(--c-serie-${(indice % 6) + 1})`;
}

/** `{sucursal: color}`, en el orden en que llegan (el mismo en las tres). */
export function coloresDe(sucursales: { location_id: string }[]): Map<string, string> {
  return new Map(sucursales.map((s, i) => [s.location_id, colorDe(i)]));
}

/**
 * Entrar en una sucursal: elegirla en el aparato e ir a una de sus pantallas.
 *
 * Es lo mismo que elegirla en el selector de arriba. Se ofrece en cada tarjeta
 * porque es lo natural después de ver que algo va mal en ella: ir a mirarlo.
 */
export function useEntrar() {
  const { cambiar } = useCambiarDeSucursal();
  const { tenant, cambiarNegocio } = useAuth();
  const { grupo } = useAmbito();
  const navegar = useNavigate();

  return (
    sucursal: { location_id: string; nombre: string; sucursales?: number },
    destino = "/",
    // Lo que la pantalla de destino lee al abrir (el Consultor de productos
    // abre el catálogo ya buscando el producto, 2026-10-07).
    estado?: unknown,
  ) => {
    // En «Todos mis negocios» la fila es un negocio (`location_id` es su
    // tenant_id): se entra en él, y después a la pantalla (2026-10-03). Si
    // tiene varias sucursales, a la de «Todas las sucursales» que dice lo
    // mismo: la de dentro enseñaría solo una de ellas.
    if (grupo) {
      const ir = () => navegar((sucursal.sucursales ?? 1) > 1 ? paraVarias(destino) : destino);
      if (sucursal.location_id === tenant?.id) {
        ir();
        return;
      }
      void cambiarNegocio(sucursal.location_id).then(() => {
        ir();
        aviso.exito(`Negocio activo: ${sucursal.nombre}`);
      });
      return;
    }
    // Cada sucursal tiene su catálogo y sus cifras: lo de la otra se vacía, y
    // con una venta en curso se pregunta antes. Ver `useCambiarDeSucursal`.
    cambiar({ id: sucursal.location_id, nombre: sucursal.nombre }, () => {
      navegar(destino, estado === undefined ? undefined : { state: estado });
      aviso.exito(`Sucursal activa: ${sucursal.nombre}`);
    });
  };
}

/** La pantalla de «Todas las sucursales» que dice lo mismo que una de dentro. */
const PARA_VARIAS: [string, string][] = [
  ["/reportes", "/sucursales/ventas"],
  ["/inventario", "/sucursales/inventario"],
  ["/credito", "/sucursales/por-cobrar"],
  ["/por-pagar", "/sucursales/por-pagar"],
  ["/caja", "/sucursales/dinero?ver=cajas"],
  ["/cuentas", "/sucursales/dinero?ver=cuentas"],
  ["/gastos", "/sucursales/dinero?ver=gastos"],
  ["/nomina", "/sucursales/dinero?ver=nomina"],
  ["/clientes", "/sucursales/clientes"],
];

function paraVarias(destino: string): string {
  if (destino === "/") return "/sucursales";
  const camino = destino.split("?")[0] ?? destino;
  // Solo la pantalla exacta: «/credito/:cliente» (su expediente) ya es de todo el negocio.
  return PARA_VARIAS.find(([de]) => camino === de)?.[1] ?? destino;
}

/** «42,3 %». */
export function pct(valor: string | number | null | undefined, decimales = 1): string {
  if (valor === null || valor === undefined) return "—";
  return `${toNumber(valor).toLocaleString("es-VE", { maximumFractionDigits: decimales })} %`;
}

/** Cuánto subió o bajó contra el período anterior, con su flecha y su color. */
export function Variacion({
  valor,
  className,
}: {
  valor: string | null;
  className?: string;
}) {
  if (valor === null) {
    return <span className={cx("text-xs text-faint", className)}>Sin período de comparación</span>;
  }
  const n = toNumber(valor);
  const sube = n >= 0;
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold tabular",
        sube ? "bg-money-in-soft text-money-in" : "bg-money-out-soft text-money-out",
        className,
      )}
      title="Comparado con el período anterior de igual duración"
    >
      {sube ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {sube ? "+" : ""}
      {n.toLocaleString("es-VE", { maximumFractionDigits: 1 })} %
    </span>
  );
}

/** El punto de color de una sucursal. */
export function Punto({ color, className }: { color: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cx("inline-block size-2.5 shrink-0 rounded-full", className)}
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * La marca de un local: su punto de color, o el ícono de almacén si es un
 * almacén —como en el selector de sucursal (2026-10-02)—.
 */
export function MarcaDeLocal({
  tipo,
  color,
  className,
}: {
  tipo?: string;
  color: string;
  className?: string;
}) {
  return tipo === "almacen" ? (
    <Warehouse size={12} className={cx("shrink-0 text-muted", className)} aria-hidden="true" />
  ) : (
    <Punto color={color} className={className} />
  );
}

/**
 * El enlace para escribirle a alguien por WhatsApp con un mensaje ya escrito.
 *
 * El teléfono se deja solo en dígitos. Un celular venezolano guardado como
 * «0414-1234567» no lo entiende WhatsApp: necesita el código del país, así que
 * se le pone el 58 en lugar del cero.
 */
export function enlaceDeWhatsApp(telefono: string, texto: string): string | null {
  let numero = telefono.replace(/\D/g, "");
  if (numero.length === 11 && numero.startsWith("04")) numero = `58${numero.slice(1)}`;
  if (numero.length < 8) return null;
  return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
}

/** «hace 5 min», «hace 2 h», «ayer», «hace 4 días». */
export function hace(iso: string): string {
  const minutos = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return "hace un momento";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.round(horas / 24);
  if (dias === 1) return "ayer";
  if (dias < 30) return `hace ${dias} días`;
  return `el ${formatFecha(iso, { day: "numeric", month: "short" })}`;
}

/**
 * Con una sola sucursal no hay nada que juntar.
 *
 * ⚠️ Y no puede quedarse en un cartel sin salida: el siguiente paso depende del
 * plan. Sin Premium lo que falta es el plan; con Premium, abrir la segunda. Las
 * dos cosas están en Ajustes › Sucursales, que sabe enseñar cada una.
 */
export function SoloUnaSucursal() {
  const tienePremium = useTienePremium("multi_sucursal");
  const { grupo } = useAmbito();
  if (grupo) {
    return (
      <Vacio
        icono={<Building2 size={32} />}
        titulo="Usted tiene un solo negocio"
        descripcion="Este apartado reúne la información de todos sus negocios: ventas, finanzas e inventario, cada uno con sus sucursales. Se activará automáticamente al crear un segundo negocio desde el selector de negocios."
      />
    );
  }
  return (
    <Vacio
      icono={<Building2 size={32} />}
      titulo="Su negocio tiene una sola sucursal"
      descripcion={
        tienePremium
          ? "Este apartado reúne la información de todas sus sucursales: ventas, finanzas e inventario. Se activará automáticamente al abrir una segunda sucursal."
          : "Este apartado reúne la información de todas sus sucursales: ventas, finanzas e inventario. La gestión de varias sucursales forma parte del plan Premium."
      }
      accion={
        <Link to="/ajustes/sucursales">
          <Boton variante={tienePremium ? "secundario" : "primario"}>
            {tienePremium ? "Abrir una sucursal" : "Conocer el plan Premium"}
          </Boton>
        </Link>
      }
    />
  );
}

// =============================================================================
//  LAS OPCIONES DENTRO DE UNA PESTAÑA (2026-10-02)
// =============================================================================

/**
 * La opción elegida dentro de una pestaña («Existencias», «Gastos»…), en la
 * dirección (`?ver=`): así «atrás» vuelve a ella y se puede mandar el enlace.
 */
export function useOpcion<T extends string>(valores: readonly T[], porDefecto: T): [T, (v: T) => void] {
  const [params, setParams] = useSearchParams();
  const leida = params.get("ver") as T | null;
  const valor = leida && valores.includes(leida) ? leida : porDefecto;
  return [
    valor,
    (v) => {
      const siguiente = new URLSearchParams(params);
      if (v === porDefecto) siguiente.delete("ver");
      else siguiente.set("ver", v);
      setParams(siguiente, { replace: true });
    },
  ];
}

/**
 * Los botones de opciones de una pestaña. En el celular la fila se desliza de
 * lado: cinco opciones no caben en 360 px y partirlas en dos filas las
 * desordena.
 */
export function Opciones<T extends string>({
  valor,
  onChange,
  opciones,
  etiquetaAria,
}: {
  valor: T;
  onChange: (v: T) => void;
  opciones: { valor: T; etiqueta: string }[];
  etiquetaAria: string;
}) {
  return (
    <div className="sin-barra -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <Segmentado
        opciones={opciones}
        valor={valor}
        onChange={onChange}
        etiquetaAria={etiquetaAria}
        className="min-w-max"
      />
    </div>
  );
}

/**
 * «Todas · Sede principal · Norte · …»: ver todas juntas o una sola. Cada
 * sucursal con su punto de color, el mismo de las gráficas.
 */
export function BotonesDeSucursal({
  sucursales,
  valor,
  onChange,
  colores,
  todas,
  extra,
}: {
  sucursales: { location_id: string; nombre: string; tipo?: string }[];
  /** `null` = todas. */
  valor: string | null;
  onChange: (id: string | null) => void;
  colores: Map<string, string>;
  todas?: string;
  /** Algo pequeño al lado del nombre: un monto, un conteo. */
  extra?: (location_id: string | null) => ReactNode;
}) {
  const { u } = useAmbito();
  const boton = (id: string | null, contenido: ReactNode) => {
    const activo = valor === id;
    return (
      <button
        key={id ?? "todas"}
        type="button"
        aria-pressed={activo}
        onClick={() => onChange(id)}
        className={cx(
          "pulsable-suave inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
          activo
            ? "border-accent bg-accent-soft text-accent"
            : "border-line bg-surface text-ink-soft hover:border-accent/50",
        )}
      >
        {contenido}
        {extra && <span className={cx("tabular", activo ? "text-accent" : "text-muted")}>{extra(id)}</span>}
      </button>
    );
  };
  return (
    <div className="sin-barra -mx-4 flex gap-1.5 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
      {boton(null, todas ?? u.Todas)}
      {sucursales.map((s) =>
        boton(
          s.location_id,
          <>
            {s.tipo === "almacen" ? (
              <Warehouse size={12} aria-hidden="true" />
            ) : (
              <Punto color={colores.get(s.location_id) ?? ""} className="size-2" />
            )}
            {s.nombre}
          </>,
        ),
      )}
    </div>
  );
}

/**
 * Una fila con su barra repartida en los colores de cada sucursal: un
 * producto, una categoría, un método de pago. Debajo, lo de cada una.
 */
export function FilaRepartida({
  puesto,
  nombre,
  valor,
  detalle,
  partes,
  colores,
  formatoParte,
}: {
  puesto?: number;
  nombre: string;
  valor: string;
  detalle?: string;
  partes: { location_id: string; nombre?: string; total: string; etiqueta?: string; tipo?: string }[];
  colores: Map<string, string>;
  formatoParte: (p: { total: string; etiqueta?: string }) => string;
}) {
  const total = partes.reduce((s, p) => s + toNumber(p.total), 0);
  return (
    <li className="border-t border-line py-3.5 first:border-t-0">
      <div className="flex items-baseline justify-between gap-4">
        <p className="flex min-w-0 items-baseline gap-2.5">
          {puesto !== undefined && (
            <span className="tabular w-5 shrink-0 text-right text-xs font-semibold text-faint">{puesto}</span>
          )}
          <span className="truncate text-sm font-medium text-ink" title={nombre}>
            {nombre}
          </span>
        </p>
        <p className="shrink-0 text-right">
          <span className="tabular text-sm font-semibold text-ink">{valor}</span>
          {detalle && <span className="tabular ml-2 text-xs text-muted">{detalle}</span>}
        </p>
      </div>
      <div className={cx("mt-2 flex h-1.5 overflow-hidden rounded-full bg-surface-2", puesto !== undefined && "ml-7")}>
        {partes.map((p) => (
          <div
            key={p.location_id}
            title={p.nombre ? `${p.nombre}: ${formatoParte(p)}` : formatoParte(p)}
            style={{
              width: `${total > 0 ? (toNumber(p.total) / total) * 100 : 0}%`,
              backgroundColor: colores.get(p.location_id),
            }}
          />
        ))}
      </div>
      {partes.length > 1 && (
        <p className={cx("mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted", puesto !== undefined && "ml-7")}>
          {partes.map((p) => (
            <span key={p.location_id} className="flex items-center gap-1.5">
              <MarcaDeLocal tipo={p.tipo} color={colores.get(p.location_id) ?? ""} className="size-2" />
              {p.nombre}
              <span className="tabular text-ink-soft">{formatoParte(p)}</span>
            </span>
          ))}
        </p>
      )}
    </li>
  );
}

/** El encabezado de una columna de las tablas de «Todas». */
export function Columna({ children, derecha = false }: { children?: ReactNode; derecha?: boolean }) {
  return (
    <th className={cx("whitespace-nowrap px-3 py-3 font-medium text-muted first:pl-4 sm:first:pl-6", derecha && "text-right")}>
      {children}
    </th>
  );
}

/** El nombre de un local con su marca: el punto de color, o el ícono si es almacén. */
export function NombreDeLocal({
  s,
  colores,
}: {
  s: { location_id: string; nombre: string; tipo?: string };
  colores: Map<string, string>;
}) {
  return (
    <span className="flex items-center gap-2 whitespace-nowrap font-medium text-ink">
      <MarcaDeLocal tipo={s.tipo} color={colores.get(s.location_id) ?? ""} className="size-2" />
      {s.nombre}
    </span>
  );
}
